// Purchases API client. See api/src/routes/purchases.ts.
//
// Mirrors the sales-api shape for symmetry — same BillRow + drill-in
// pattern. Vendor-name field is intentionally omitted; this DB doesn't
// track it on the bill. REFBILL_NO carries the vendor's invoice
// reference (free text).

export interface PurchaseBillRow {
  billNo: string;
  billDate: string;
  storeCode: string;
  netAmt: number;
  billAmt: number;
  tender: string;
  refBillNo: string;
  enteredBy: string;
}

export interface PurchaseReturnRow {
  billNo: string;
  billDate: string;
  storeCode: string;
  netAmt: number;
  billAmt: number;
  tender: string;
}

export interface PurchasesPage {
  page: number; limit: number; total: number;
  bills: PurchaseBillRow[];
}
export interface PurchaseReturnsPage {
  page: number; limit: number; total: number;
  returns: PurchaseReturnRow[];
}

export interface PurchaseSummary {
  from: string; to: string; store: string;
  purchases: { count: number; value: number };
  returns:   { count: number; value: number };
  daily: Array<{ date: string; value: number; bills: number }>;
}

export interface PurchaseLine {
  barcode: string;
  rowrollNo: string;
  itemCode: string;
  itemName: string;
  color: string | null;
  size: string | null;
  /** Article / style code, from the line or the catalog view. */
  style: string | null;
  qty: number;
  mrp: number;
  pp: number;
  fDscntAmt: number;
  mdAmt: number;
  dscntAmt: number;
  netAmt: number;
}

export interface PurchaseDetail {
  kind: 'purchase' | 'return';
  header: {
    billNo: string; billDate: string; storeCode: string;
    refBillNo: string | null;
    enteredBy: string | null;
    tender: string | null;
  };
  totals: {
    netAmt: number; fDscntAmt: number; mdAmt: number; dscntAmt: number;
    vat1: number; vat2: number; vat3: number;
    billAmt: number; freight: number;
  };
  payment: {
    cashAmt: number; cardAmt: number; cashCredit: string | null;
  };
  lines: PurchaseLine[];
}

function qs(p: Record<string, string | number | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== '') u.set(k, String(v));
  return u.toString();
}

export async function getPurchaseSummary(p: { from: string; to: string; store?: string | null }): Promise<PurchaseSummary> {
  const r = await fetch(`/api/purchases/summary?${qs({ from: p.from, to: p.to, store: p.store ?? undefined })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`purchases summary failed (${r.status})`);
  return r.json() as Promise<PurchaseSummary>;
}

export async function getPurchaseBills(p: {
  from: string; to: string; store?: string | null;
  page: number; limit: number; q?: string | null;
}): Promise<PurchasesPage> {
  const r = await fetch(`/api/purchases/bills?${qs({
    from: p.from, to: p.to, store: p.store ?? undefined,
    page: p.page, limit: p.limit, q: p.q ?? undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`purchase bills failed (${r.status})`);
  return r.json() as Promise<PurchasesPage>;
}

export async function getPurchaseReturns(p: {
  from: string; to: string; store?: string | null;
  page: number; limit: number; q?: string | null;
}): Promise<PurchaseReturnsPage> {
  const r = await fetch(`/api/purchases/returns?${qs({
    from: p.from, to: p.to, store: p.store ?? undefined,
    page: p.page, limit: p.limit, q: p.q ?? undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`purchase returns failed (${r.status})`);
  return r.json() as Promise<PurchaseReturnsPage>;
}

export async function getPurchaseDetail(p: {
  storeCode: string; billDate: string; billNo: string;
  store?: string | null;
}): Promise<PurchaseDetail> {
  const url = `/api/purchases/bills/${encodeURIComponent(p.storeCode)}/${encodeURIComponent(p.billDate)}/${encodeURIComponent(p.billNo)}?${qs({ store: p.store ?? undefined })}`;
  const r = await fetch(url, { credentials: 'include' });
  if (r.status === 404) throw new Error('NOT_FOUND');
  if (r.status === 403) throw new Error('FORBIDDEN');
  if (!r.ok) throw new Error(`purchase detail failed (${r.status})`);
  return r.json() as Promise<PurchaseDetail>;
}
